import React from "react";
import { ArrowRight, MessageCircle, CheckCircle2, Star } from "lucide-react";
import { SITE, buildWhatsAppLink, getContact } from "@/lib/site";
import { useContent } from "@/lib/content";

export default function Hero({ onConsult }) {
  const { content } = useContent();
  const h = content.hero || {};
  const contact = getContact(content);
  const heroImg = h.image_url || SITE.media.teamWithLogo;

  const trust = h.trust_badges || ["100% Pass Support", "Placement Assistance", "EMI Available"];

  return (
    <section id="hero" data-testid="hero-section" className="relative bg-[#f8fafc] overflow-hidden">
      <div className="absolute inset-0 bg-grid-soft opacity-50 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 md:px-8 pt-12 pb-16 md:pt-20 md:pb-28 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-7 reveal in-view">
          <div className="inline-flex items-center gap-2 bg-[#73c3ff]/15 text-[#002570] px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase mb-6">
            <Star className="w-3.5 h-3.5 fill-[#002570]" />
            {h.eyebrow || "From Admission to Placement — We Stay With You"}
          </div>
          <h1 className="font-rubik text-4xl sm:text-5xl lg:text-[64px] leading-[1.05] font-extrabold tracking-tight text-[#0f172a] text-balance">
            {h.headline_pre || "Confused After +2 or Degree?"}{" "}
            <span className="text-[#002570]">{h.headline_emphasis || "We Guide You"}</span> {h.headline_post || "to a Global Career."}
          </h1>
          <p className="mt-6 font-inter text-base md:text-lg text-slate-600 max-w-2xl leading-relaxed">
            {h.subhead || "ACCA · CMA USA · CA · CS · CFA — career programs with one-to-one mentoring, retake support and 100% guidance till you pass and get placed."}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button
              onClick={onConsult}
              data-testid="hero-book-consultation-btn"
              className="group inline-flex items-center justify-center gap-2 bg-[#002570] text-white hover:bg-[#001a52] rounded-full px-7 py-4 font-inter font-medium text-sm md:text-base transition-all hover:shadow-xl hover:shadow-[#002570]/20"
            >
              Book Free Career Consultation
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <a
              href={buildWhatsAppLink("Hi! I want to talk to a student mentor about courses at Educators Institute.", contact.whatsappRaw)}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-whatsapp-mentor-link"
              className="inline-flex items-center justify-center gap-2 bg-white border-2 border-slate-200 text-[#002570] hover:border-[#73c3ff] hover:bg-[#73c3ff]/10 rounded-full px-7 py-4 font-inter font-medium text-sm md:text-base transition-all"
            >
              <MessageCircle className="w-4 h-4" />
              Talk to Student Mentor
            </a>
          </div>

          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3">
            {trust.map((t) => (
              <div key={t} className="flex items-center gap-2 text-sm font-inter text-slate-700">
                <CheckCircle2 className="w-4 h-4 text-[#002570]" />
                <span className="font-medium">{t}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 reveal in-view">
          <div className="relative">
            <div className="absolute -top-6 -left-6 w-32 h-32 rounded-3xl bg-[#73c3ff]/30 -z-10" />
            <div className="absolute -bottom-6 -right-6 w-40 h-40 rounded-3xl bg-[#002570]/10 -z-10" />
            <div className="relative aspect-[4/5] w-full rounded-[2rem] overflow-hidden shadow-2xl border-8 border-white">
              <img src={heroImg} alt="Educators Institute mentor team" className="w-full h-full object-cover" />
              <div className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur-sm rounded-2xl p-4 shadow-lg flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-[#002570] text-white flex items-center justify-center text-sm font-bold font-rubik">
                  {h.badge_count || "500+"}
                </div>
                <div>
                  <p className="font-rubik text-sm font-bold text-[#0f172a]">{h.badge_label || "Students Mentored"}</p>
                  <p className="font-inter text-xs text-slate-600">{h.badge_sublabel || "By a team that stays till you pass"}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
